// Searching and matching titles for Call Sheet's guess input.
//
// **A guess is always a selected title record, never raw typing** (CLAUDE.md).
// Submit stays disabled until what is typed resolves to a title — either picked
// from the dropdown or typed as an exact match under the forgiving comparison.
//
// Matching is forgiving about form but not about wording: capitals, punctuation,
// accents and "&" vs "and" are ignored, misspellings are not. That rule lives in
// `shared/normaliseTitle`, which the nightly sync uses to build the stored search
// keys — so the website and the database compare the same way, and there is one
// implementation rather than two that can drift.
//
// ---
//
// **The dropdown is a property of the letters typed, never of the puzzle.** The
// same letters give the same list in the same order whichever puzzle is open.
// That was not true, and the way it failed is worth keeping: the answer was
// *prepended* to the list so it would always be findable, which on any puzzle
// whose answer the list did not already hold put the answer at the top of the
// dropdown. Typing "C" on the Collateral puzzle offered Collateral first. A
// dropdown that ranks the answer highest is a dropdown that answers the puzzle.
//
// **The browser no longer knows the answer while a game is being played** -- the
// server sends it only once the game is over (cs_play) -- so the searchable set
// is the title list and nothing else, and nothing about the puzzle can reach the
// dropdown at all. The list is every listed title (public/titles.json, built
// from the film list); an answer that has dropped out of the list would not be
// typeable, and none has (checked when the list was first built).
//
// **The dropdown matches the title a player can see, not its alternates.**
// Alternates are still accepted when typed in full (`resolve`), which is the
// rule — but they are not searched, because a row cannot be explained by
// anything on screen. Typing "Co" was offering *The Godfather*, whose alternate
// is "The Godfather The Coppola Restoration"; typing "C" was offering
// *Inception*, by way of the Welsh "Dechreuad y Creu". Both are correct by the
// old rule and inexplicable to the person reading them.
import { normaliseTitle } from '../../../shared/normalise.ts';

export interface TitleRecord {
  id: number;
  kind: 'movie' | 'tv';
  title: string;          // what is displayed, always
  year: number | null;
  /** The other forms it answers to -- alternative titles, already normalised
   *  in the list file. Matched against, never displayed. */
  alt?: string[];
  /** Well known: 1,000 votes or more (scripts/build-title-list.ts). Listed
   *  first in the dropdown. */
  known?: boolean;
  /** Fame order: the title's place in the list, which is built most-voted
   *  first. Where two titles share a name, the better known comes first. */
  rank?: number;
}

export const MAX_SUGGESTIONS = 8;

/**
 * **Every row says Movie or Show beside its year** (owner, 13 October), in the
 * dropdown and the Guesses list alike, so the two always match. It used to be
 * only the rows whose name another title shares — the 2003 *Daredevil*, the
 * series — on the argument that a tag on every row is a tag nobody reads; the
 * owner chose one rule everywhere over that.
 */
/** The word shown beside the year. The site's own, not TMDB's `tv`. */
export const kindLabel = (kind: 'movie' | 'tv') => (kind === 'tv' ? 'Show' : 'Movie');

/** A leading article, which is not how anybody looks a title up: somebody
 *  hunting The Dark Knight types "dark" as readily as "the dark". */
const ARTICLE = /^(the|a|an) /;

/**
 * The form a title is ranked and sorted on: normalised, with a leading article
 * dropped. So *The Dark Knight* files under D and answers to "dark".
 */
export function sortKey(title: string): string {
  return normaliseTitle(title).replace(ARTICLE, '');
}

// Every form a title answers to: the one shown, plus its alternates. Used for
// resolving what has been typed, never for searching.
function keysOf(t: TitleRecord): string[] {
  return [t.title, ...(t.alt ?? [])].map(normaliseTitle).filter(Boolean);
}

// A title is its kind and id together — an id alone is not unique, because 105
// is Back to the Future and it is also Sex and the City.
export function sameTitle(a: Pick<TitleRecord, 'id' | 'kind'>, b: Pick<TitleRecord, 'id' | 'kind'>): boolean {
  return a.id === b.id && a.kind === b.kind;
}

/**
 * **Spaces do not count** (owner, 12 October). The normaliser already turns
 * hyphens, apostrophes, colons, the middle dot and accents into spaces or
 * nothing, so *Spider-Man* is "spider man" — and "Spiderman", typed as one
 * word, found only *Japanese Spiderman*. Squashing the spaces out of both sides
 * makes Spiderman, Spider Man and Spider-Man one thing, and the same for X-Men,
 * WALL·E, Ocean's Eleven and Amélie.
 */
export const squash = (normalised: string) => normalised.replace(/ /g, '');

// Each title's squashed forms from every word start, worked out once: the
// search runs over 28,000 titles on every keystroke.
const startsCache = new Map<string, { whole: string[]; later: string[] }>();
function starts(title: string): { whole: string[]; later: string[] } {
  let hit = startsCache.get(title);
  if (hit) return hit;
  const full = normaliseTitle(title);
  const words = full.split(' ');
  const later: string[] = [];
  for (let i = 1; i < words.length; i++) later.push(words.slice(i).join(''));
  hit = { whole: [squash(full), squash(full.replace(ARTICLE, ''))], later };
  startsCache.set(title, hit);
  return hit;
}

/**
 * Which group a title falls in, or null for no match.
 *
 * **0 — the title starts with what was typed**, ignoring a leading article.
 * **1 — a later word starts with what was typed.** So "guy" finds *Free Guy*.
 *
 * Word starts only: no match from the middle of a word, because a substring
 * search over 25,000 titles returns everything containing a common letter run,
 * and no fuzzy tolerance, because a near-miss on a title is a different title.
 * Both sides are compared without spaces, so a match may run on across word
 * boundaries ("spiderman" finds *Spider-Man*) but must still begin at one.
 */
export function group(title: string, query: string): 0 | 1 | null {
  const q = squash(query);
  if (!q) return null;
  const { whole, later } = starts(title);
  // Both forms, so that "the god" finds The Godfather and "god" does too.
  if (whole.some((w) => w.startsWith(q))) return 0;
  if (later.some((w) => w.startsWith(q))) return 1;
  return null;
}

export function search(raw: string, list: TitleRecord[]): TitleRecord[] {
  const query = normaliseTitle(raw);
  if (!query) return [];
  const hits: { t: TitleRecord; w: 0 | 1; g: 0 | 1; k: string }[] = [];
  for (const t of list) {
    const g = group(t.title, query);
    if (g !== null) hits.push({ t, w: t.known ? 0 : 1, g, k: sortKey(t.title) });
  }
  // **Well-known titles first, then the rest**, and inside each the order it
  // has always had: starts-with before later-word, alphabetical, and the id
  // last so that two titles with the same name never swap places between
  // renders. Alphabetical alone, over 27,908 titles, offered "È arrivato mio
  // fratello" for "e". The whole set is ordered before it is cut: truncating
  // first would cut by whatever order the list happened to arrive in. Whether a
  // title is well known is a fact about the title, never about the puzzle, so
  // the same letters still give the same rows on every puzzle. **Two titles of
  // the same name come best known first** (`rank`, the list's fame order), and
  // the id is the last tie-break so nothing swaps places between renders.
  hits.sort((a, b) => a.w - b.w || a.g - b.g || a.k.localeCompare(b.k)
    || (a.t.rank ?? 0) - (b.t.rank ?? 0) || a.t.id - b.t.id);
  return hits.slice(0, MAX_SUGGESTIONS).map((h) => h.t);
}

// What the typed text resolves to, or null. An exact match on any of a title's
// forms — the forgiving comparison, not a prefix: a player who has typed enough
// to be reading a dropdown has not yet chosen anything. Alternates count here,
// which is what keeps "Avengers Assemble" a correct way to say The Avengers.
//
// **Only where exactly one title answers to it** (owner, 12 October). Typed
// text names a title, not a record, and "Hannibal" is a film and a series:
// taking whichever came first in the list is what marked correct answers wrong.
// Where the name is shared the player picks from the dropdown, which carries
// the record itself (TitleInput.tsx).
export function resolve(raw: string, list: TitleRecord[]): TitleRecord | null {
  const typed = squash(normaliseTitle(raw));
  if (!typed) return null;
  let found: TitleRecord | null = null;
  for (const t of list) {
    if (!keysOf(t).some((k) => squash(k) === typed)) continue;
    if (found) return null;              // shared name: the dropdown decides
    found = t;
  }
  return found;
}

// Looking a guess back up for display. A guess is stored as an id and a kind,
// and shown by the title's own display name — never by what the player typed.
// The answer, once the server has sent it, is looked in as well, so a correct
// guess is named even if its title has since left the list.
export function byId(id: number, kind: 'movie' | 'tv', list: TitleRecord[], answer?: TitleRecord | null): TitleRecord | null {
  if (answer && sameTitle(answer, { id, kind })) return answer;
  return list.find((t) => sameTitle(t, { id, kind })) ?? null;
}
